import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './view.scss';

const ZOOM_MIN           = 1;
const ZOOM_MAX           = 4;
const ZOOM_DBLCLICK_STEP = 2.5;
const ZOOM_BUTTON_STEP   = 0.5;
const ZOOM_WHEEL_RATE    = 0.0015;

/**
 * Wires pinch/scroll/double-click/drag zoom + pan onto a lightbox image,
 * plus its +/-/reset buttons. One instance is created per gallery-mode
 * carousel's modal.
 */
function initLightboxZoom( { modalEl, viewport, img, zoomInBtn, zoomOutBtn, zoomResetBtn } ) {
	const state = { scale: 1, x: 0, y: 0 };
	const pointers = new Map();

	let pinchStartDistance = 0;
	let pinchStartScale    = 1;
	let panPointerId       = null;
	let panStart           = null;
	let rafId              = null;

	function applyTransform() {
		img.style.transform = `translate(${ state.x }px, ${ state.y }px) scale(${ state.scale })`;
		clampPan();
		viewport.classList.toggle( 'gc-zoomed', state.scale > 1.001 );
	}

	function scheduleApply() {
		if ( rafId ) return;
		rafId = requestAnimationFrame( () => {
			rafId = null;
			applyTransform();
		} );
	}

	// Keeps the image from being panned/zoomed entirely out of the viewport:
	// centers it on any axis where it's smaller than the viewport, and pins
	// its edges flush on any axis where it's larger.
	function clampPan() {
		const vRect = viewport.getBoundingClientRect();
		const iRect = img.getBoundingClientRect();
		let dx = 0;
		let dy = 0;

		if ( iRect.width <= vRect.width ) {
			dx = ( vRect.left + vRect.width / 2 ) - ( iRect.left + iRect.width / 2 );
		} else if ( iRect.left > vRect.left ) {
			dx = vRect.left - iRect.left;
		} else if ( iRect.right < vRect.right ) {
			dx = vRect.right - iRect.right;
		}

		if ( iRect.height <= vRect.height ) {
			dy = ( vRect.top + vRect.height / 2 ) - ( iRect.top + iRect.height / 2 );
		} else if ( iRect.top > vRect.top ) {
			dy = vRect.top - iRect.top;
		} else if ( iRect.bottom < vRect.bottom ) {
			dy = vRect.bottom - iRect.bottom;
		}

		if ( dx || dy ) {
			state.x += dx;
			state.y += dy;
			img.style.transform = `translate(${ state.x }px, ${ state.y }px) scale(${ state.scale })`;
		}
	}

	// Zooms to newScale while keeping the image point under (clientX, clientY)
	// fixed on screen — works from the image's own current rect, so it's
	// unaffected by centering/layout offsets.
	function zoomAt( clientX, clientY, newScale ) {
		newScale = Math.min( ZOOM_MAX, Math.max( ZOOM_MIN, newScale ) );
		const iRect = img.getBoundingClientRect();
		const imgX  = ( clientX - iRect.left ) / state.scale;
		const imgY  = ( clientY - iRect.top ) / state.scale;
		const newLeft = clientX - imgX * newScale;
		const newTop  = clientY - imgY * newScale;

		state.x += newLeft - iRect.left;
		state.y += newTop - iRect.top;
		state.scale = newScale;
	}

	function reset() {
		state.scale = 1;
		state.x = 0;
		state.y = 0;
		img.classList.add( 'gc-no-transition' );
		applyTransform();
		requestAnimationFrame( () => img.classList.remove( 'gc-no-transition' ) );
	}

	viewport.addEventListener( 'wheel', ( e ) => {
		e.preventDefault();
		img.classList.add( 'gc-no-transition' );
		const factor = Math.exp( -e.deltaY * ZOOM_WHEEL_RATE );
		zoomAt( e.clientX, e.clientY, state.scale * factor );
		scheduleApply();
	}, { passive: false } );

	viewport.addEventListener( 'dblclick', ( e ) => {
		img.classList.remove( 'gc-no-transition' );
		if ( state.scale > 1.001 ) {
			reset();
		} else {
			zoomAt( e.clientX, e.clientY, ZOOM_DBLCLICK_STEP );
			applyTransform();
		}
	} );

	viewport.addEventListener( 'pointerdown', ( e ) => {
		viewport.setPointerCapture( e.pointerId );
		pointers.set( e.pointerId, { x: e.clientX, y: e.clientY } );

		if ( pointers.size === 2 ) {
			const pts = [ ...pointers.values() ];
			pinchStartDistance = Math.hypot( pts[ 0 ].x - pts[ 1 ].x, pts[ 0 ].y - pts[ 1 ].y );
			pinchStartScale = state.scale;
			panPointerId = null;
		} else if ( pointers.size === 1 && state.scale > 1.001 ) {
			panPointerId = e.pointerId;
			panStart = { x: e.clientX, y: e.clientY, sx: state.x, sy: state.y };
			viewport.classList.add( 'gc-panning' );
		}
	} );

	viewport.addEventListener( 'pointermove', ( e ) => {
		if ( ! pointers.has( e.pointerId ) ) return;
		pointers.set( e.pointerId, { x: e.clientX, y: e.clientY } );

		if ( pointers.size === 2 ) {
			const pts = [ ...pointers.values() ];
			const distance = Math.hypot( pts[ 0 ].x - pts[ 1 ].x, pts[ 0 ].y - pts[ 1 ].y );
			const midX = ( pts[ 0 ].x + pts[ 1 ].x ) / 2;
			const midY = ( pts[ 0 ].y + pts[ 1 ].y ) / 2;
			if ( pinchStartDistance > 0 ) {
				img.classList.add( 'gc-no-transition' );
				zoomAt( midX, midY, pinchStartScale * ( distance / pinchStartDistance ) );
				scheduleApply();
			}
		} else if ( panPointerId === e.pointerId && panStart ) {
			img.classList.add( 'gc-no-transition' );
			state.x = panStart.sx + ( e.clientX - panStart.x );
			state.y = panStart.sy + ( e.clientY - panStart.y );
			scheduleApply();
		}
	} );

	function endPointer( e ) {
		pointers.delete( e.pointerId );
		if ( e.pointerId === panPointerId ) {
			panPointerId = null;
			panStart = null;
			viewport.classList.remove( 'gc-panning' );
		}
		if ( pointers.size < 2 ) {
			pinchStartDistance = 0;
		}
		img.classList.remove( 'gc-no-transition' );
	}
	viewport.addEventListener( 'pointerup', endPointer );
	viewport.addEventListener( 'pointercancel', endPointer );

	function zoomByStep( delta ) {
		img.classList.remove( 'gc-no-transition' );
		const vRect = viewport.getBoundingClientRect();
		zoomAt( vRect.left + vRect.width / 2, vRect.top + vRect.height / 2, state.scale + delta );
		applyTransform();
	}

	if ( zoomInBtn )    zoomInBtn.addEventListener( 'click', () => zoomByStep( ZOOM_BUTTON_STEP ) );
	if ( zoomOutBtn )   zoomOutBtn.addEventListener( 'click', () => zoomByStep( -ZOOM_BUTTON_STEP ) );
	if ( zoomResetBtn ) zoomResetBtn.addEventListener( 'click', reset );

	// Every lightbox open should start fit-to-frame, regardless of how the
	// previous view was left.
	modalEl.addEventListener( 'hidden.bs.modal', reset );
}

function initCarousels() {
	// :not selector prevents double-init if called multiple times.
	document.querySelectorAll( '.gc-carousel-swiper:not([data-swiper-initialized])' ).forEach( ( el ) => {
		el.dataset.swiperInitialized = 'true';

		const raw = el.dataset.swiperConfig;
		if ( ! raw ) return;

		let cfg;
		try {
			cfg = JSON.parse( raw );
		} catch ( e ) {
			// eslint-disable-next-line no-console
			console.error( 'GC Carousel: invalid config JSON', e );
			return;
		}

		const modules = [ A11y ];
		if ( cfg.navigation ) modules.push( Navigation );
		if ( cfg.pagination ) modules.push( Pagination );
		if ( cfg.autoplay )   modules.push( Autoplay );

		new Swiper( el, {
			modules,
			slidesPerView:  cfg.slidesPerView  ?? 1,
			slidesPerGroup: cfg.slidesPerGroup  ?? 1,
			spaceBetween:   cfg.spaceBetween    ?? 0,
			speed:          cfg.speed           ?? 500,
			loop:           cfg.loop            ?? false,
			navigation: cfg.navigation ? {
				nextEl: el.querySelector( '.swiper-button-next' ),
				prevEl: el.querySelector( '.swiper-button-prev' ),
			} : false,
			pagination: cfg.pagination ? {
				el:        el.querySelector( '.swiper-pagination' ),
				clickable: true,
			} : false,
			autoplay: cfg.autoplay ? {
				delay:                cfg.autoplayDelay     ?? 3000,
				pauseOnMouseEnter:    cfg.pauseOnMouseEnter ?? false,
				disableOnInteraction: false,
			} : false,
			breakpoints: cfg.breakpoints ?? {},
			dir:  cfg.rtl ? 'rtl' : 'ltr',
			a11y: { enabled: true },
		} );

		if ( cfg.gallery ) {
			el.classList.add( 'gc-gallery-mode' );

			const modalEl = document.getElementById( `${ el.id }-modal` );
			if ( modalEl && window.bootstrap && window.bootstrap.Modal ) {
				const modalImg = modalEl.querySelector( '.gc-lightbox-img' );
				const viewport = modalEl.querySelector( '.gc-lightbox-viewport' );

				el.querySelectorAll( '.gc-slide-img' ).forEach( ( img ) => {
					img.addEventListener( 'click', () => {
						modalImg.src = img.currentSrc || img.src;
						modalImg.alt = img.alt;
						window.bootstrap.Modal.getOrCreateInstance( modalEl ).show();
					} );
				} );

				if ( viewport && modalImg ) {
					initLightboxZoom( {
						modalEl,
						viewport,
						img: modalImg,
						zoomInBtn:    modalEl.querySelector( '.gc-zoom-in' ),
						zoomOutBtn:   modalEl.querySelector( '.gc-zoom-out' ),
						zoomResetBtn: modalEl.querySelector( '.gc-zoom-reset' ),
					} );
				}
			}
		}
	} );
}

// Call immediately — carousel markup is already in the DOM when this
// footer script executes. DOMContentLoaded is a safety net for edge cases
// (e.g. defer attribute applied by WordPress).
initCarousels();
document.addEventListener( 'DOMContentLoaded', initCarousels );
